import dynamic from 'next/dynamic';

const SiteUrl = dynamic(() => import('../components/SiteUrl'), {
  loading: () => null,
  ssr: false
});
function PrivacyPolicy() {
  return (
    <section>
      <div className="cms-pages ">
        <div className="container">
          <h1 className="display-h1 text-center">Privacy Policy</h1>
          <h2 className="heading-h1">PRIVACY NOTICE</h2>
          <strong className="label-l">Last updated June 17, 2020</strong>
          <p>
            Thank you for choosing to be part of our community at The Relevate
            Group, doing business as Breitbart ("Breitbart", "we", "us", "our").
            We are committed to protecting your personal information and your
            right to privacy. If you have any questions or concerns about this
            privacy notice, or our practices with regards to your personal
            information, please contact us at privacy@breitbart.com.
          </p>
          <p>
            When you visit our website <SiteUrl /> (the "Website"), and more
            generally, use any of our services (the "Services", which include
            the Website), we appreciate that you are trusting us with your
            personal information. We take your privacy very seriously. In this
            privacy notice, we seek to explain to you in the clearest way
            possible what information we collect, how we use it and what rights
            you have in relation to it. We hope you take some time to read
            through it carefully, as it is important. If there are any terms in
            this privacy notice that you do not agree with, please discontinue
            use of our Services immediately.
          </p>
          <p>
            This privacy notice applies to all information collected through our
            Services (which, as described above, includes our Website), as well
            as, any related services, sales, marketing or events.
          </p>
          <strong className="button-l">
            This privacy notice applies to all information collected through our
            Services (which, as described above, includes our Website), as well
            as, any related services, sales, marketing or events.
          </strong>
          <h2 className="heading-h1">TABLE OF CONTENTS</h2>
          <ul>
            <li>
              <a href="#1">1. WHAT INFORMATION DO WE COLLECT?</a>
            </li>
            <li>
              <a href="#2"> 2. WILL YOUR INFORMATION BE SHARED WITH ANYONE?</a>
            </li>
            <li>
              <a href="#3">
                3. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
              </a>
            </li>
            <li>
              <a href="#4">
                4. IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?
              </a>
            </li>
            <li>
              <a href="#5">5. WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?</a>
            </li>
            <li>
              <a href="#6">6. HOW LONG DO WE KEEP YOUR INFORMATION?</a>
            </li>
            <li>
              <a href="#7"> 7. HOW DO WE KEEP YOUR INFORMATION SAFE? </a>
            </li>
            <li>
              <a href="#8">8. DO WE COLLECT INFORMATION FROM MINORS?</a>
            </li>
            <li>
              <a href="#9">9. WHAT ARE YOUR PRIVACY RIGHTS?</a>
            </li>
            <li>
              <a href="#10">10. CONTROLS FOR DO-NOT-TRACK FEATURES </a>
            </li>
            <li>
              <a href="#11">
                11. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
              </a>
            </li>
            <li>
              <a href="#12">12. DO WE MAKE UPDATES TO THIS NOTICE? </a>
            </li>
            <li>
              <a href="#13">13. HOW CAN YOU CONTACT US ABOUT THIS NOTICE? </a>
            </li>
            <li>
              <a href="#14">
                14. HOW CAN YOU REVIEW, UPDATE OR DELETE THE DATA WE COLLECT
                FROM YOU?
              </a>
            </li>
          </ul>
          <h2 id="1" className="heading-h1">
            1. WHAT INFORMATION DO WE COLLECT?
          </h2>
          <h3> Personal information you disclose to us</h3>
          <p>
            <strong>In Short:</strong> We collect personal information that you
            provide to us.
          </p>
          <p>
            We collect personal information that you voluntarily provide to us
            when you register on the Website, express an interest in obtaining
            information about us or our products and Services, when you
            participate in activities on the Website or otherwise when you
            contact us.
          </p>
          <p>
            The personal information that we collect depends on the context of
            your interactions with us and the Website, the choices you make and
            the products and features you use. The personal information we
            collect may include the following:
          </p>
          <p>
            <strong>Personal Information Provided by You.</strong> We collect
            financial information (credit card number, purchase history,
            invoices); passwords; and other similar information.
          </p>
          <p>
            <strong>Payment Data.</strong> We may collect data necessary to
            process your payment if you make purchases, such as your payment
            instrument number (such as a credit card number), and the security
            code associated with your payment instrument. All payment data is
            stored by Stripe. You may find their privacy notice link(s) here:
            https://stripe.com/privacy.
          </p>

          <p>
            All personal information that you provide to us must be true,
            complete and accurate, and you must notify us of any changes to such
            personal information.
          </p>
          <h3> Information automatically collected</h3>

          <p>
            In Short: Some information — such as your Internet Protocol (IP)
            address and/or browser and device characteristics — is collected
            automatically when you visit our Website.
          </p>
          <p>
            We automatically collect certain information when you visit, use or
            navigate the Website. This information does not reveal your specific
            identity (like your name or contact information) but may include
            device and usage information, such as your IP address, browser and
            device characteristics, operating system, language preferences,
            referring URLs, device name, country, location, information about
            how and when you use our Website and other technical information.
            This information is primarily needed to maintain the security and
            operation of our Website, and for our internal analytics and
            reporting purposes.
          </p>
          <p>
            Like many businesses, we also collect information through cookies
            and similar technologies.
          </p>

          <h2 id="2" className="heading-h1">
            2. WILL YOUR INFORMATION BE SHARED WITH ANYONE?
          </h2>
          <p>
            In Short: We only share information with your consent, to comply
            with laws, to provide you with services, to protect your rights, or
            to fulfill business obligations.
          </p>
          <p>
            We may process or share your data that we hold based on the
            following legal basis:
          </p>
          <ul className="disc">
            <li>
              Consent: We may process your data if you have given us specific
              consent to use your personal information for a specific purpose.
            </li>
            <li>
              Legitimate Interests: We may process your data when it is
              reasonably necessary to achieve our legitimate business interests.
            </li>
            <li>
              Performance of a Contract: Where we have entered into a contract
              with you, we may process your personal information to fulfill the
              terms of our contract.
            </li>
            <li>
              Legal Obligations: We may disclose your information where we are
              legally required to do so in order to comply with applicable law,
              governmental requests, a judicial proceeding, court order, or
              legal process, such as in response to a court order or a subpoena
              (including in response to public authorities to meet national
              security or law enforcement requirements).
            </li>
            <li>
              Vital Interests: We may disclose your information where we believe
              it is necessary to investigate, prevent, or take action regarding
              potential violations of our policies, suspected fraud, situations
              involving potential threats to the safety of any person and
              illegal activities, or as evidence in litigation in which we are
              involved.
            </li>
          </ul>
          <p>
            More specifically, we may need to process your data or share your
            personal information in the following situations:
          </p>
          <ul className="disc">
            <li>
              Business Transfers. We may share or transfer your information in
              connection with, or during negotiations of, any merger, sale of
              company assets, financing, or acquisition of all or a portion of
              our business to another company.
            </li>
          </ul>
          <h2 id="3" className="heading-h1">
            3. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
          </h2>
          <p>
            <strong>In Short:</strong> We may use cookies and other tracking
            technologies to collect and store your information.
          </p>
          <p>
            We may use cookies and similar tracking technologies (like web
            beacons and pixels) to access or store information. Specific
            information about how we use such technologies and how you can
            refuse certain cookies is set out in our Cookie Notice.
          </p>

          <h2 id="4" className="heading-h1">
            4. IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?
          </h2>
          <p>
            <strong>In Short:</strong> We may transfer, store, and process your
            information in countries other than your own.
          </p>
          <p>
            Our servers are located in the United States. If you are accessing
            our Website from outside the United States, please be aware that
            your information may be transferred to, stored, and processed by us
            in our facilities and by those third parties with whom we may share
            your personal information (see "WILL YOUR INFORMATION BE SHARED WITH
            ANYONE?" above), in the United States, and other countries.
          </p>
          <p>
            If you are a resident in the European Economic Area (EEA) or United
            Kingdom (UK), then these countries may not necessarily have data
            protection laws or other similar laws as comprehensive as those in
            your country. We will however take all necessary measures to protect
            your personal information in accordance with this privacy notice and
            applicable law.
          </p>

          <h2 id="5" className="heading-h1">
            5. WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?
          </h2>
          <p>
            <strong>In Short:</strong> We are not responsible for the safety of
            any information that you share with third-party providers who
            advertise, but are not affiliated with, our Website.
          </p>
          <p>
            The Website may contain advertisements from third parties that are
            not affiliated with us and which may link to other websites, online
            services or mobile applications. We cannot guarantee the safety and
            privacy of data you provide to any third parties. Any data collected
            by third parties is not covered by this privacy notice. We are not
            responsible for the content or privacy and security practices and
            policies of any third parties, including other websites, services or
            applications that may be linked to or from the Website. You should
            review the policies of such third parties and contact them directly
            to respond to your questions.
          </p>

          <h2 id="6" className="heading-h1">
            6. HOW LONG DO WE KEEP YOUR INFORMATION?
          </h2>
          <p>
            <strong>In Short:</strong> We keep your information for as long as
            necessary to fulfill the purposes outlined in this privacy notice
            unless otherwise required by law.
          </p>
          <p>
            We will only keep your personal information for as long as it is
            necessary for the purposes set out in this privacy notice, unless a
            longer retention period is required or permitted by law (such as
            tax, accounting or other legal requirements). No purpose in this
            notice will require us keeping your personal information for longer
            than .
          </p>
          <p>
            When we have no ongoing legitimate business need to process your
            personal information, we will either delete or anonymize such
            information, or, if this is not possible (for example, because your
            personal information has been stored in backup archives), then we
            will securely store your personal information and isolate it from
            any further processing until deletion is possible.
          </p>

          <h2 id="7" className="heading-h1">
            7. HOW DO WE KEEP YOUR INFORMATION SAFE?
          </h2>
          <p>
            <strong>In Short:</strong> We aim to protect your personal
            information through a system of organizational and technical
            security measures.
          </p>
          <p>
            We have implemented appropriate technical and organizational
            security measures designed to protect the security of any personal
            information we process. However, despite our safeguards and efforts
            to secure your information, no electronic transmission over the
            Internet or information storage technology can be guaranteed to be
            100% secure, so we cannot promise or guarantee that hackers,
            cybercriminals, or other unauthorized third parties will not be able
            to defeat our security, and improperly collect, access, steal, or
            modify your information. Although we will do our best to protect
            your personal information, transmission of personal information to
            and from our Website is at your own risk. You should only access the
            Website within a secure environment.
          </p>

          <h2 id="8" className="heading-h1">
            8. DO WE COLLECT INFORMATION FROM MINORS?
          </h2>
          <p>
            <strong>In Short:</strong> We aim to protect your personal
            information through a system of organizational and technical
            security measures.
          </p>
          <p>
            We have implemented appropriate technical and organizational
            security measures designed to protect the security of any personal
            information we process. However, despite our safeguards and efforts
            to secure your information, no electronic transmission over the
            Internet or information storage technology can be guaranteed to be
            100% secure, so we cannot promise or guarantee that hackers,
            cybercriminals, or other unauthorized third parties will not be able
            to defeat our security, and improperly collect, access, steal, or
            modify your information. Although we will do our best to protect
            your personal information, transmission of personal information to
            and from our Website is at your own risk. You should only access the
            Website within a secure environment.
          </p>

          <h2 id="9" className="heading-h1">
            9. WHAT ARE YOUR PRIVACY RIGHTS?
          </h2>
          <p>
            In Short: In some regions, such as the European Economic Area (EEA)
            and United Kingdom (UK), you have rights that allow you greater
            access to and control over your personal information. You may
            review, change, or terminate your account at any time.
          </p>
          <p>
            In some regions (like the EEA and UK), you have certain rights under
            applicable data protection laws. These may include the right (i) to
            request access and obtain a copy of your personal information, (ii)
            to request rectification or erasure; (iii) to restrict the
            processing of your personal information; and (iv) if applicable, to
            data portability. In certain circumstances, you may also have the
            right to object to the processing of your personal information. To
            make such a request, please use the{' '}
            <a
              href="https://app.termly.io/embed/terms-of-use/a0325d18-bae5-43fb-9c24-a522b2bc5595#contact"
              target="_blank"
              rel="noreferrer"
            >
              contact details
            </a>{' '}
            provided below. We will consider and act upon any request in
            accordance with applicable data protection laws.
          </p>

          <p>
            If we are relying on your consent to process your personal
            information, you have the right to withdraw your consent at any
            time. Please note however that this will not affect the lawfulness
            of the processing before its withdrawal, nor will it affect the
            processing of your personal information conducted in reliance on
            lawful processing grounds other than consent.
          </p>

          <p>
            If you are a resident in the EEA or UK and you believe we are
            unlawfully processing your personal information, you also have the
            right to complain to your local data protection supervisory
            authority. You can find their contact details here:
            <a
              href="http://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm"
              target="_blank"
              rel="noreferrer"
            >
              http://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm.
            </a>
          </p>
          <p>
            If you are a resident in Switzerland, the contact details for the
            data protection authorities are available here:
            <a
              href="https://www.edoeb.admin.ch/edoeb/en/home.html"
              target="_blank"
              rel="noreferrer"
            >
              https://www.edoeb.admin.ch/edoeb/en/home.html.
            </a>
          </p>
          <p>
            If you have questions or comments about your privacy rights, you may
            email us at webmaster@breitbart.com.
          </p>
          <h3>contact details</h3>
          <p>
            If you would at any time like to review or change the information in
            your account or terminate your account, you can:
          </p>
          <ul className="disc">
            <li>
              Log into your account settings and update your user account.
            </li>
            <li>Contact us using the contact information provided.</li>
          </ul>
          <p>
            Upon your request to terminate your account, we will deactivate or
            delete your account and information from our active databases.
            However, we may retain some information in our files to prevent
            fraud, troubleshoot problems, assist with any investigations,
            enforce our Terms of Use and/or comply with applicable legal
            requirements.
          </p>
          <p>
            <strong>Cookies and similar technologies:</strong> Most Web browsers
            are set to accept cookies by default. If you prefer, you can usually
            choose to set your browser to remove cookies and to reject cookies.
            If you choose to remove cookies or reject cookies, this could affect
            certain features or services of our Website. To opt-out of
            interest-based advertising by advertisers on our Website visit
            http://www.aboutads.info/choices/.
          </p>
          <p>
            <strong>Opting out of email marketing:</strong> You can unsubscribe
            from our marketing email list at any time by clicking on the
            unsubscribe link in the emails that we send or by contacting us
            using the details provided below. You will then be removed from the
            marketing email list — however, we may still communicate with you,
            for example to send you service-related emails that are necessary
            for the administration and use of your account, to respond to
            service requests, or for other non-marketing purposes. To otherwise
            opt-out, you may:
          </p>
          <ul className="disc">
            <li>Contact us using the contact information provided.</li>
            <li>Access your account settings and update preferences.</li>
          </ul>
          <h2 id="10" className="heading-h1">
            10. CONTROLS FOR DO-NOT-TRACK FEATURES
          </h2>
          <p>
            Most web browsers and some mobile operating systems and mobile
            applications include a Do-Not-Track ("DNT") feature or setting you
            can activate to signal your privacy preference not to have data
            about your online browsing activities monitored and collected. At
            this stage no uniform technology standard for recognizing and
            implementing DNT signals has been finalized. As such, we do not
            currently respond to DNT browser signals or any other mechanism that
            automatically communicates your choice not to be tracked online. If
            a standard for online tracking is adopted that we must follow in the
            future, we will inform you about that practice in a revised version
            of this privacy notice.
          </p>
          <h2 id="11" className="heading-h1">
            11. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
          </h2>
          <p>
            <strong>In Short:</strong> Yes, if you are a resident of California,
            you are granted specific rights regarding access to your personal
            information.
          </p>
          <p>
            California Civil Code Section 1798.83, also known as the "Shine The
            Light" law, permits our users who are California residents to
            request and obtain from us, once a year and free of charge,
            information about categories of personal information (if any) we
            disclosed to third parties for direct marketing purposes and the
            names and addresses of all third parties with which we shared
            personal information in the immediately preceding calendar year. If
            you are a California resident and would like to make such a request,
            please submit your request in writing to us using the contact
            information provided below.
          </p>
          <p>
            If you are under 18 years of age, reside in California, and have a
            registered account with the Website, you have the right to request
            removal of unwanted data that you publicly post on the Website. To
            request removal of such data, please contact us using the contact
            information provided below, and include the email address associated
            with your account and a statement that you reside in California. We
            will make sure the data is not publicly displayed on the Website,
            but please be aware that the data may not be completely or
            comprehensively removed from all our systems (e.g. backups, etc.).
          </p>

          <h2 id="12" className="heading-h1">
            12. DO WE MAKE UPDATES TO THIS NOTICE?
          </h2>
          <p>
            In Short: Yes, we will update this notice as necessary to stay
            compliant with relevant laws.
          </p>
          <p>
            We may update this privacy notice from time to time. The updated
            version will be indicated by an updated "Revised" date and the
            updated version will be effective as soon as it is accessible. If we
            make material changes to this privacy notice, we may notify you
            either by prominently posting a notice of such changes or by
            directly sending you a notification. We encourage you to review this
            privacy notice frequently to be informed of how we are protecting
            your information.
          </p>

          <h2 id="13" className="heading-h1">
            13. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
          </h2>
          <p>
            If you have questions or comments about this notice, you may contact
            our Data Protection Officer (DPO), Rob McCloskey, by email at
            copyright@breitbart.com, or by post to:
          </p>
          <br />
          <br />
          <p>The Relevate Group</p>
          <p>Rob McCloskey</p>
          <p>PO Box 922</p>
          <p>Franklin, TN 37064</p>
          <p>United States</p>

          <h2 id="14" className="heading-h1">
            14. HOW CAN YOU REVIEW, UPDATE OR DELETE THE DATA WE COLLECT FROM
            YOU?
          </h2>
          <p>
            Based on the applicable laws of your country, you may have the right
            to request access to the personal information we collect from you,
            change that information, or delete it in some circumstances. To
            request to review, update, or delete your personal information,
            please submit a request form by{' '}
            <a
              href="https://app.termly.io/notify/a0325d18-bae5-43fb-9c24-a522b2bc5595"
              target="_blank"
              rel="noreferrer"
            >
              clicking here
            </a>
            .
          </p>
        </div>
      </div>
    </section>
  );
}
export default PrivacyPolicy;
